import React, { useEffect, useState } from "react";
import { DashboardLayout } from "../common/page_component/index.js";
import Loader from "../common/Loader.js";
import Alert from "../common/Alert.js";
import Button from "../common/Button.js";
import agentPartnershipService from "../../services/agentPartnershipService.js";
import agentDirectoryService from "../../services/agentDirectoryService.js";
import { useAuth } from "../../context/AuthContext.js";
import { useTranslation } from 'react-i18next';


const MyPartnerships = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [partnerships, setPartnerships] = useState([]);
  const [pending, setPending] = useState([]);
  const [profile, setProfile] = useState({
    is_visible: false,
    service_cities: [],
    bio: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [allRes, pendingRes, profileRes] = await Promise.all([
        agentPartnershipService.list(),
        agentPartnershipService.list({ scope: "incoming", status: "pending" }),
        agentDirectoryService.getMyProfile(),
      ]);
      setPartnerships(allRes.data?.data || []);
      setPending(pendingRes.data?.data || []);
      if (profileRes.data?.data) setProfile(profileRes.data.data);
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to load partnerships");
    } finally {
      setLoading(false);
    }
  };

  const respond = async (partnershipId, action) => {
    try {
      await agentPartnershipService.respond(partnershipId, { action });
      Alert.success(`Request ${action}ed`);
      loadData();
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to respond");
    }
  };

  const saveProfile = async () => {
    try {
      await agentDirectoryService.updateMyProfile({
        is_visible: profile.is_visible,
        service_cities: profile.service_cities,
        bio: profile.bio,
      });
      Alert.success("Directory profile updated");
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to update profile");
    }
  };

  if (loading) return <DashboardLayout><Loader /></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <h1 className="text-xl font-semibold">{t('pages.agentNetwork.myPartnerships')}</h1>

        <div className="rounded-lg border bg-white p-4 space-y-3">
          <h2 className="font-medium">Directory Profile</h2>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={profile.is_visible} onChange={(e) => setProfile({ ...profile, is_visible: e.target.checked })} />
            Visible to other companies
          </label>
          <input
            className="w-full rounded-md border px-3 py-2 text-sm"
            placeholder="Service cities (comma separated)"
            value={(profile.service_cities || []).join(", ")}
            onChange={(e) => setProfile({ ...profile, service_cities: e.target.value.split(",").map((v) => v.trim()).filter(Boolean) })}
          />
          <textarea
            className="w-full rounded-md border px-3 py-2 text-sm"
            placeholder="Short bio"
            value={profile.bio || ""}
            onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
          />
          <Button variant="primary" onClick={saveProfile}>Save Profile</Button>
        </div>

        <div className="rounded-lg border bg-white p-4">
          <h2 className="font-medium mb-3">Pending Requests</h2>
          {pending.length === 0 ? (
            <p className="text-sm text-gray-500">No pending requests</p>
          ) : pending.map((item) => (
            <div key={item.partnership_id} className="flex flex-wrap items-center justify-between gap-2 border-t py-3">
              <div>
                <p className="text-sm font-medium">{item.requesting_company_name}</p>
                <p className="text-xs text-gray-500">{item.service_city}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="success" onClick={() => respond(item.partnership_id, "accept")}>Accept</Button>
                <Button size="sm" variant="danger" onClick={() => respond(item.partnership_id, "reject")}>Reject</Button>
              </div>
            </div>
          ))}
        </div>

        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3">{t('common.company')}</th>
                <th className="px-4 py-3">City</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {partnerships.length === 0 ? (
                <tr><td colSpan="3" className="px-4 py-6 text-center text-gray-500">No partnerships yet</td></tr>
              ) : partnerships.map((item) => (
                <tr key={item.partnership_id} className="border-t">
                  <td className="px-4 py-3">{item.requesting_company_name}</td>
                  <td className="px-4 py-3">{item.service_city}</td>
                  <td className="px-4 py-3 capitalize">{item.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MyPartnerships;
